import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MultiSelect, ReactSelect, SpinnerLoader } from "@services/ui-components";
import AddEditModal from "@services/ui-components/src/AddEditModal/AddEditModal";
import { Formik, Form, ErrorMessage } from "formik";
import TextInput from "@services/ui-components/src/TextInput/TextInput";
import { Option, OptionString } from "../../../../../interfaces/interfaces";
import { MultiOption } from "@services/ui-components/src/MultiSelect/MultiSelect";
import { LanguageVersions, PartialMenu } from "../../../../../interfaces/meMenuInterfaces/interfaces";
import { AsyncFn, useMenuSubmitters } from "../../hooks/useMenuSubmitters";
import { useSubmitByMode } from "../../hooks/useSubmitByMode";
import { menuFormValidationSchema } from "../../helpers";
import { MenuInitSync } from "./MenuInitSync";
import { cloneNameToDisplay } from "../../../utils/cloneNameUtils";
import { MenuStatusType, MenuTypeType } from "@me-team/host/main/graphql/types";

export interface MenuInitialValues {
    name: string;
    type: MenuTypeType | null;
    languages: number[];
    status: MenuStatusType | null;
}

interface AddEditMenuModalProps {
    show: boolean;
    mode: "create" | "edit" | "clone" | "restore" | null;
    onClose: () => void;
    menu?: PartialMenu;
    onSubmit?: (menuData: any) => void;
    languageVersionOptions: Option[];
    menuTypeOptions: OptionString[];
    primaryLanguage: LanguageVersions;
    onRefetch?: AsyncFn;
    onSuccessToast?: (text: string) => void;
}

const AddEditMenuModal: React.FC<AddEditMenuModalProps> = ({
    show,
    mode,
    onClose,
    menu,
    languageVersionOptions,
    menuTypeOptions,
    primaryLanguage,
    onRefetch,
    onSuccessToast,
}) => {
    const { t } = useTranslation();

    const initialValues: MenuInitialValues = {
        name: "",
        type: null,
        languages: [],
        status: null,
    };

    let title = "";
    let saveButtonText = "";

    switch (mode) {
        case "create":
            title = t("New Menu");
            saveButtonText = t("Create");
            break;
        case "edit":
            title = t("Edit Menu");
            saveButtonText = t("Save");
            break;
        case "clone":
            title = `${t("Cloning menu")} "${cloneNameToDisplay(menu?.name, t)}"`;
            saveButtonText = t("Cloning");
            break;
        case "restore":
            title = t("Restore Menu");
            saveButtonText = t("Restore");
            break;
        default:
            break;
    }

    const submitters = useMenuSubmitters({
        onClose,
        refetch: onRefetch,
        onSuccess: (msg: string) => onSuccessToast?.(msg),
    });
    const handleCreateSubmit = useSubmitByMode(mode, submitters, menu, () => {});

    return (
        <AddEditModal isModalOpened={show} setIsModalOpened={onClose} title={t(title)} textAlign="start">
            <>
                <Formik
                    initialValues={initialValues}
                    validationSchema={menuFormValidationSchema(t)}
                    onSubmit={handleCreateSubmit}
                >
                    {({ isSubmitting, setFieldValue, handleReset, values }) => {
                        return (
                            <>
                                <MenuInitSync
                                    mode={mode}
                                    menu={menu}
                                    languageVersionOptions={languageVersionOptions}
                                    defaultStatusForCreate={MenuStatusType.Activated}
                                    ensurePrimaryLanguageId={primaryLanguage?.id ?? null}
                                />
                                <Form>
                                    <Row className="align-items-center m-0 mb-3 mb-sm-4">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="modalMenu-name-field"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Menu name")}
                                                </span>
                                            </label>
                                        </Col>
                                        <TextInput
                                            id="modalMenu-name-field"
                                            inputCol={9}
                                            name="name"
                                            label=""
                                            placeholder="Add name menu"
                                            isLabel={false}
                                            maxLength={50}
                                            showCounter
                                        />
                                    </Row>
                                    <Row className="align-items-center m-0 mb-3 mb-sm-4">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="createMenu-type-field"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Menu type")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <div className="position-relative">
                                                <ReactSelect
                                                    id="createMenu-type-field"
                                                    name="type"
                                                    placeholder={t("Select menu type")}
                                                    options={menuTypeOptions}
                                                    value={
                                                        menuTypeOptions.find(
                                                            (option) => option.value === values.type
                                                        ) || null
                                                    }
                                                    onChange={(selectedOption: OptionString | null) =>
                                                        setFieldValue("type", selectedOption?.value ?? null)
                                                    }
                                                    isSearchable
                                                    disabled={mode === "edit" || mode === "restore"}
                                                    errorIconRightOffset="49px"
                                                />
                                                <OverlayTrigger
                                                    placement="left"
                                                    overlay={
                                                        <Tooltip>
                                                            <div className="text-start">
                                                                <p className="m-0">
                                                                    {t(
                                                                        "Delivery menu – for online orders with delivery."
                                                                    )}
                                                                </p>
                                                                <p className="m-0">
                                                                    {t(
                                                                        "Dine-in menu – for offline service at the venue."
                                                                    )}
                                                                </p>
                                                                <p className="m-0">
                                                                    {t(
                                                                        "Pickup menu – for online orders that customers pick up themselves."
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div className="position-absolute top-50 translate-middle-y end-0 me-4">
                                                        <i className="bi bi-info-circle pe-2"></i>
                                                    </div>
                                                </OverlayTrigger>
                                            </div>
                                            <ErrorMessage name="type">
                                                {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-center m-0 mb-3 mb-sm-4">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="createMenu-languages-field"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Language versions")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <div className="position-relative " style={{ height: "49px" }}>
                                                <MultiSelect
                                                    id="createMenu-languages-field"
                                                    name="languages"
                                                    placeholder={t("Select language versions of the menu")}
                                                    options={languageVersionOptions ?? []}
                                                    value={languageVersionOptions.filter((option) =>
                                                        (values.languages ?? []).includes(option.value)
                                                    )}
                                                    onChange={(selectedOption: MultiOption[]) =>
                                                        setFieldValue(
                                                            "languages",
                                                            selectedOption
                                                                ? selectedOption.map((option) => option.value)
                                                                : []
                                                        )
                                                    }
                                                    isSearchable
                                                    lockValues={[primaryLanguage?.id]}
                                                />
                                                <OverlayTrigger
                                                    placement="left"
                                                    overlay={
                                                        <Tooltip>
                                                            {t("The translation can be added during editing")}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div className="position-absolute top-50 translate-middle-y end-0 me-4">
                                                        <i className="bi bi-info-circle pe-2"></i>
                                                    </div>
                                                </OverlayTrigger>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-center m-0 mb-3 mb-sm-4">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="createMenu-languageMain-field"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Main language menu")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control fs-7"
                                                    value={primaryLanguage?.name ?? ""}
                                                    disabled
                                                />
                                                <OverlayTrigger
                                                    placement="left"
                                                    overlay={
                                                        <Tooltip>
                                                            {t(
                                                                "The main language can be edited in the company settings"
                                                            )}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div className="position-absolute top-50 translate-middle-y end-0 me-4">
                                                        <i className="bi bi-info-circle pe-2"></i>
                                                    </div>
                                                </OverlayTrigger>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end m-0 pt-2 gap-3">
                                        <Button
                                            onClick={() => {
                                                handleReset();
                                                onClose();
                                            }}
                                            variant="outline-primary"
                                            type="button"
                                            disabled={isSubmitting}
                                            className="custom-btn fw-normal"
                                        >
                                            {t("Cancel")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="custom-btn"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                            ) : (
                                                t(saveButtonText)
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </>
        </AddEditModal>
    );
};

export default AddEditMenuModal;
